/**
 * Settings Context
 * Manages app-wide settings like dark mode and debug mode
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import { STORAGE_KEYS } from '@constants/storage-keys';

// Create a sample context value to derive the type from
const _sampleSettingsContextValue = {
  darkMode: false,
  debugMode: false,
  toggleDarkMode: (() => {}) as () => void,
  toggleDebugMode: (() => {}) as () => void,
};

// Derived type from sample context value
type SettingsContextValue = typeof _sampleSettingsContextValue;

const SettingsContext = createContext<SettingsContextValue | null>(null);

export function SettingsProvider({ children }: { children: ReactNode }) {
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.DARK_MODE);
    return saved ? saved === 'true' : true; // Default to dark mode
  });

  const [debugMode, setDebugMode] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.DEBUG_MODE);
    // Default to true in development, false in production
    return saved ? saved === 'true' : import.meta.env.DEV;
  });

  // Apply theme to document
  useEffect(() => {
    if (darkMode) {
      // Dark mode: remove data-theme attribute (dark is default)
      delete document.documentElement.dataset.theme;
    } else {
      // Light mode: set data-theme="light"
      document.documentElement.dataset.theme = 'light';
    }
  }, [darkMode]);

  const toggleDarkMode = useCallback(() => {
    setDarkMode((prev: boolean) => {
      const newValue = !prev;
      localStorage.setItem(STORAGE_KEYS.DARK_MODE, String(newValue));
      return newValue;
    });
  }, []);

  const toggleDebugMode = useCallback(() => {
    setDebugMode((prev: boolean) => {
      const newValue = !prev;
      localStorage.setItem(STORAGE_KEYS.DEBUG_MODE, String(newValue));
      return newValue;
    });
  }, []);

  const value = useMemo(
    () => ({
      darkMode,
      debugMode,
      toggleDarkMode,
      toggleDebugMode,
    }),
    [darkMode, debugMode, toggleDarkMode, toggleDebugMode]
  );

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
}

/**
 * Hook to access settings context
 */
export function useSettings() {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within SettingsProvider');
  }
  return context;
}
